<template>
  <div>
    <load-profile />
    <!-- Table Container Card -->
    <b-card>
      <validation-observer
        ref="createForm"
        #default="{ invalid }"
      >
        <!-- form -->
        <n-form-confirmation
          key="createForm"
          :form="$refs.createForm"
          :disabled="invalid"
          @submit="submit"
        >
          <n-input
            v-model="data"
            :fields="fields"
          >
            <template #name>
              <validation-provider
                v-slot="{ errors }"
                :name="$t('field.name')"
                :rules="
                  locale === $i18n.locale.split('-')[0]
                    ? 'required|max:100'
                    : 'max:100'
                "
                style="width: 100%"
              >
                <b-form-group :label="$t('field.name')">
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1 mr-1">
                      <b-form-input
                        v-model="data.name[locale]"
                        :placeholder="$t('field.name')"
                        :state="errors.length === 0 ? null : false"
                      />
                    </div>
                    <div style="min-width: 80px">
                      <b-form-select
                        v-model="locale"
                        :options="locales"
                        class="language-select"
                      />
                    </div>
                  </div>
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </template>
            <template #abilities>
              <permission-form
                v-model="data.abilities"
                :permission-group="permissions"
              />
            </template>
          </n-input>
          <b-row>
            <b-col
              cols="12"
              class="text-right"
            >
              <n-button-loading
                v-if="$can('create', permissionType)"
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="submit"
                class="mt-1 mr-1"
                variant="primary"
                :loading="loading"
                :disabled="invalid || !data.name[locale]"
              >
                {{ $t('button.saveChanges') }}
              </n-button-loading>
              <n-button-loading
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-secondary"
                :loading="loading"
                class="mt-1"
                @submit="back"
              >
                {{ $t('button.cancel') }}
              </n-button-loading>
            </b-col>
          </b-row>
        </n-form-confirmation>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BCard,
  BCardText,
  BCardHeader,
  BCardTitle,
  BLink,
  BFormRadioGroup,
  BFormCheckbox,
  BFormSelect,
} from 'bootstrap-vue'
import Repository from '@/repositories/RepositoryFactory'
import Ripple from 'vue-ripple-directive'
import NButtonLoading from '@/components/NButtonLoading'
import NFormConfirmation from '@/components/NFormConfirmation'
import NInput from '@/components/NInput'
import NTable from '@/components/NTable'
import { mapValidationMessage } from '@/libs/helper'
import formInput from './formInput'
import PermissionForm from './PermissionForm.vue'

const RoleRepository = Repository.get('role')
const PermissionRepository = Repository.get('permission')

export default {
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BCard,
    BCardText,
    BCardHeader,
    BCardTitle,
    BLink,
    BFormRadioGroup,
    BFormCheckbox,
    NButtonLoading,
    NFormConfirmation,
    NInput,
    NTable,
    PermissionForm,
    BFormSelect,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      data: {
        name: {
          en: '',
          zh: '',
          km: '',
          vi: '',
        },
        description: null,
        abilities: [],
      },
      permissions: [],
      loading: false,
      locale: this.$i18n.locale.split('-')[0],
      locales: [
        { value: 'en', text: 'English' },
        { value: 'zh', text: '中文' },
        { value: 'km', text: 'ខ្មែរ' },
        { value: 'vi', text: 'Tiếng Anh' },
      ],
    }
  },
  watch: {
    '$i18n.locale': function (val) {
      const [language] = val.split('-')
      this.locale = language
    },
  },
  mounted() {
    this.getPermission()
  },
  methods: {
    formatErrorMessage(message) {
      return message ? this.$t(message) : ''
    },
    // Helper method to convert string name to object
    convertNameToObject(name) {
      if (typeof name === 'string') {
        const defaultName = name
        const nameObj = {
          en: defaultName,
          zh: '',
          km: '',
          vi: '',
        }
        // Set the current locale's value
        const currentLocale = this.$i18n.locale.split('-')[0]
        if (['en', 'zh', 'km', 'vi'].includes(currentLocale)) {
          nameObj[currentLocale] = defaultName
        }
        return nameObj
      }
      return name
    },
    getPermission() {
      PermissionRepository.index()
        .then(response => {
          const data = [...response.data.data]
          const groups = []
          data.forEach(row => {
            if (!groups[row.module]) {
              groups[row.module] = []
            }
            groups[row.module].push(row)
          })
          const ingorePermissions = [
            'view-plan',
            'cancel-plan',
            'change-plan',
          ]
          const permissions = []
          const existPermissions = []
          for (const row of data) {
            const isIgnore = ingorePermissions.includes(row.code)
            if (isIgnore) {
              continue
            }

            const index = existPermissions.findIndex(
              item => item == row.module,
            )
            if (index === -1) {
              permissions.push({
                module: row.module,
                permissions: groups[row.module],
              })
              existPermissions.push(row.module)
            }
          }
          this.permissions = [...permissions]
        })
        .catch()
        .then(() => {})
    },
    submit() {
      this.$refs.createForm.validate().then(success => {
        if (success) {
          this.save()
        }
      })
    },
    save() {
      this.loading = true

      // Ensure name is an object before submission
      const formData = { ...this.data }
      formData.name = this.convertNameToObject(formData.name)

      RoleRepository.create(formData)
        .then(response => {
          this.$router.push({
            name: `list-${this.route}`,
          })
        })
        .catch(error => {
          if (error.response?.status === 422) {
            const errorMessage = mapValidationMessage(
              error.response.data.validate,
            )
            this.$refs.createForm.setErrors(errorMessage)
          }
        })
        .then(() => {
          this.loading = false
        })
    },
    back() {
      this.$router.back()
    },
  },
  setup() {
    const fields = formInput
    const permissionType = 'manage-role'
    const route = 'role'
    return { fields, permissionType, route }
  },
}
</script>

<style lang="scss" scoped>
.language-select {
  height: 100%;
}
</style>
